import React, { useState } from 'react';
import { authService } from '../../services/authService';

interface ConfirmationFormProps {
  email: string;
  onSuccess: () => void;
  onBackToSignup: () => void;
}

export const ConfirmationForm: React.FC<ConfirmationFormProps> = ({ 
  email, 
  onSuccess, 
  onBackToSignup 
}) => {
  const [confirmationCode, setConfirmationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      await authService.confirmSignUp(email, confirmationCode);
      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Verification failed';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await authService.resendConfirmationCode(email);
      setSuccessMessage('Verification code sent! Check your email.');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resend code';
      setError(errorMessage);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="confirmation-form-container">
      <div className="confirmation-form">
        <h2>Verify Your Email</h2>
        <p>
          We've sent a verification code to <strong>{email}</strong>. 
          Please enter the code below to complete your registration.
        </p>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="confirmationCode">Verification Code</label>
            <input
              type="text"
              id="confirmationCode"
              name="confirmationCode"
              value={confirmationCode}
              onChange={(e) => {
                setConfirmationCode(e.target.value);
                setError(null);
              }}
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
              disabled={isLoading}
            />
          </div>

          <button
            type="submit"
            disabled={isLoading || !confirmationCode.trim()}
            className="confirm-button"
          >
            {isLoading ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>

        <div className="resend-section">
          <p>Didn't receive the code?</p>
          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResending}
            className="link-button"
          >
            {isResending ? 'Sending...' : 'Resend Code'}
          </button>
        </div>

        <div className="back-link">
          <button
            type="button"
            onClick={onBackToSignup}
            className="link-button"
            disabled={isLoading}
          >
            ← Back to Sign Up
          </button>
        </div>
      </div>
    </div>
  );
};